import {isMobile} from '../utils/utils'
import {isBefore} from 'date-fns'
import {DateUtils} from './date-utils'
import {DeliverySlot} from '../types/store/basket'
import {ShopperCustomers} from 'commerce-api'
import {months} from '../constants'

/**
 * Compare to delivery slot strings to determine if they have the same
 * delivery slot identifier (ignoring any dynamic values)
 * @param slotA First slot to compare
 * @param slotB Second slot to compare
 * @returns Boolean result
 */
export const slotsMatch = (slotA: string, slotB: string) => {
  return slotA?.split('_')[0] === slotB?.split('_')[0]
}

/**
 * Extract the main part of then slot ignoring the encoded data
 * @param slot {string} Slot to extract
 * @returns {string}
 */
export const slotBaseId = (slot: string) => {
  return slot?.split('_')[0]
}

/**
 * Scrolls the Booking page to a specific slot and tab
 * @param {string} preBookSlotID the ID of the slot to scroll to
 * @param {string} clickTab the ID of the tab to click
 * @returns {null}
 */
export const scrollToBooking = (preBookSlotID: string, clickTab = '') => {
  if (!preBookSlotID) return
  if (clickTab) {
    const tabElement: HTMLElement = document.querySelector(
      `[data-calendar-tab-id="${clickTab}"]`,
    ) as HTMLElement

    if (tabElement) {
      tabElement?.click()
    }
  }

  const slotElement = document.querySelector(`[data-slot-id="${preBookSlotID}"]`)

  if (slotElement) {
    // Have to adjust for the floating header on mobile
    const headerHeight = document.querySelector('header')?.getBoundingClientRect().height || 0
    const offset = isMobile() ? headerHeight : 0

    const topPosition = slotElement.getBoundingClientRect().top
    const leftPosition = slotElement.getBoundingClientRect().left

    window.scrollTo({top: topPosition - offset, left: leftPosition, behavior: 'smooth'})
  } else {
    document
      .querySelector('[data-test-selector="book-slot-grid"]')
      ?.scrollIntoView({behavior: 'smooth'})
  }
}

/**
 * Check if a prebook slot has expired
 * @param expiryTime The c_slotExpiryTime from the prebooked order
 * @returns Boolean - true if expired, false if still valid
 */
export const isPrebookExpired = (expiryTime: string | undefined): boolean => {
  if (!expiryTime) return true

  const expiryDate = new Date(expiryTime)
  if (!DateUtils.validateDate(expiryDate)) return true

  const expiryDateZoned = DateUtils.utcToZonedDate(expiryTime)
  return isBefore(expiryDateZoned, DateUtils.current())
}

/**
 * Check if prebook delivery window falls on the given delivery day
 * @param prebookedOrder The customer's prebooked order
 * @param deliveryDay The delivery day slot to check against
 * @returns Boolean - true if prebook is for this day
 */
export const isPrebookOnDay = (
  prebookedOrder: ShopperCustomers.Order | null,
  deliveryDay: DeliverySlot,
): boolean => {
  if (!prebookedOrder?.c_windowStartTime || !deliveryDay) return false

  const prebookDate = new Date(prebookedOrder.c_windowStartTime)
  const prebookDayOfMonth = prebookDate.getDate()

  const dayMatch = deliveryDay.day.match(/\d+/)
  if (!dayMatch) return false
  const deliveryDayOfMonth = parseInt(dayMatch[0], 10)

  const prebookMonth = prebookDate.getMonth() // 0-based index
  const deliveryMonthStr = deliveryDay.month.toLowerCase().slice(0, 3)
  const deliveryMonth = months.map((m) => m.toLowerCase().slice(0, 3)).indexOf(deliveryMonthStr)

  return deliveryDayOfMonth === prebookDayOfMonth && deliveryMonth === prebookMonth
}
